<template>
    <div
        v-if="show"
        :class="componentClasses"
        @click="state = State.Opened"
        @keydown.enter="onGreetingClicked()"
    >
        {{ message }}
        <div
            class="delayed-greeting__close-icon"
            @click.stop="manuallyHideButton"
            @keydown.esc="manuallyHideButton"
        >
            <close-icon />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import CloseIcon from '@keyvue/one-awesome-icons/icons/solid/xmark.svg?component';
    import useConfiguration from '@/composables/useConfiguration';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import { VisitorInteractionType } from '@keyvue/one-api-client';
    import useVisitorData from '@/composables/useVisitorData';

    const { state } = useWidgetState();
    const { assistent } = useConfiguration();
    const { registerInteraction } = useVisitorData();
    const show = ref(false);

    const componentClasses = computed(() => {
        return {
            'delayed-greeting': true,
            'delayed-greeting--open': show.value,
        };
    });

    const openAfterSeconds = assistent.value?.widget?.openAfterSeconds ?? 0;
    const message = assistent.value?.widget?.greeting ?? '';

    function manuallyHideButton(): void {
        show.value = false;
        sessionStorage.setItem('winck-ai-chat-greeting-hidden', 'true');
    }

    async function onGreetingClicked() {
        state.value = State.Opened;
        await registerInteraction(VisitorInteractionType.WIDGET_CLICKED);
    }

    onMounted(() => {
        if (openAfterSeconds > 0 && sessionStorage.getItem('winck-ai-chat-greeting-hidden') === null) {
            setTimeout(() => {
                show.value = true;
            }, openAfterSeconds * 1000);
        }
    });

    onBeforeUnmount(() => {
        show.value = false;
    });
</script>

<style scoped lang="scss">
    .delayed-greeting {
        position: relative;
        background-color: white;
        color: black;
        box-shadow: rgba(150, 150, 150, 0.2) 0 10px 30px 0, rgba(150, 150, 150, 0.2) 0 0 0 1px;
        border-radius: 10px;
        font-size: 100%;
        opacity: 1;
        width: max-content;
        margin: 0 1rem;
        padding: 1rem;
        pointer-events: auto;
        cursor: pointer;

        &--open {
            animation: showBubble 400ms ease;
        }

        &__close-icon {
            position: absolute;
            display: flex;
            right: -12px;
            top: -12px;
            align-items: center;
            justify-content: center;
            background-color: white;
            border-radius: 50%;
            border: 1px solid #96969633;
            width: 24px;
            height: 24px;

            & > svg {
                color: #a7a7a7;
                width: 16px;
                height: 16px;
            }
        }
    }

    @keyframes showBubble {
        0% {
            opacity: 0;
            transform: scale(0.7) translateY(20px);
        }
        50% {
            opacity: 1;
            transform: scale(1.3) translateY(-20px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
</style>
